import type {
  ColumnOrderState,
  ColumnVisibilityState,
  Updater,
} from '@tanstack/react-table';

import type { ConfigurableTableName, TableColumnSettings } from './types';

export const emptyColumnVisibility: ColumnVisibilityState = {};
export const emptyColumnOrder: ColumnOrderState = [];

/** The stored key that holds the per-table order, not a table name. */
const columnOrderKey = 'columnOrder';

type Assert<TCondition extends true> = TCondition;

/**
 * The blob's top level is keyed by table name *and* by `columnOrderKey`, so the
 * two must stay disjoint — a table named `columnOrder` would sit where every
 * table's order is stored and silently destroy it. `Assert` is what makes this
 * bite: a bare conditional type resolving to `never` is legal and compiles, so
 * the violation has to fail a constraint to be reported.
 */
type ColumnOrderKeyIsNotATableName = Assert<
  typeof columnOrderKey extends ConfigurableTableName ? false : true
>;
export type { ColumnOrderKeyIsNotATableName };

/** A table's hidden columns. Absent means nothing is hidden. */
export const readTableColumnVisibility = (
  settings: TableColumnSettings | null | undefined,
  tableName: ConfigurableTableName
): ColumnVisibilityState => settings?.[tableName] ?? emptyColumnVisibility;

/**
 * A table's stored order of the labeled columns. Absent means the definition
 * order — expand it with web-ui's `resolveColumnOrder` before handing it to a
 * table.
 */
export const readTableColumnOrder = (
  settings: TableColumnSettings | null | undefined,
  tableName: ConfigurableTableName
): ColumnOrderState =>
  settings?.[columnOrderKey]?.[tableName] ?? emptyColumnOrder;

/**
 * A column is stored only while it is hidden, so anything absent — including a
 * column added after the user last touched the menu — shows up by default.
 */
export const keepHiddenColumnsOnly = (
  state: ColumnVisibilityState
): ColumnVisibilityState =>
  Object.fromEntries(
    Object.entries(state).filter(([, isVisible]) => isVisible === false)
  );

/** Drops a key whose value carries nothing worth storing. */
const withoutEmpty = <TValue extends object>(
  settings: TableColumnSettings,
  key: string,
  value: TValue,
  isEmpty: (value: TValue) => boolean
): TableColumnSettings => {
  const { [key]: _previous, ...others } = settings as Record<string, unknown>;

  return (
    isEmpty(value) ? others : { ...others, [key]: value }
  ) as TableColumnSettings;
};

/**
 * Applies a table's visibility change to the stored blob, leaving the other
 * tables — and every table's stored order — untouched.
 */
export const applyTableColumnVisibility = (
  settings: TableColumnSettings,
  tableName: ConfigurableTableName,
  updater: Updater<ColumnVisibilityState>
): TableColumnSettings => {
  const current = readTableColumnVisibility(settings, tableName);
  const next = typeof updater === 'function' ? updater(current) : updater;

  return withoutEmpty(
    settings,
    tableName,
    keepHiddenColumnsOnly(next),
    (visibility) => Object.keys(visibility).length === 0
  );
};

/**
 * Applies a table's order change to the stored blob, leaving the other tables'
 * orders — and every table's hidden columns — untouched. Pass `defaultOrder`
 * (the labeled columns in definition order) so an order that merely restates
 * it is dropped rather than stored.
 */
export const applyTableColumnOrder = (
  settings: TableColumnSettings,
  tableName: ConfigurableTableName,
  updater: Updater<ColumnOrderState>,
  defaultOrder: ColumnOrderState
): TableColumnSettings => {
  const current = readTableColumnOrder(settings, tableName);
  const next = typeof updater === 'function' ? updater(current) : updater;
  const isDefault =
    next.length === defaultOrder.length &&
    next.every((id, index) => id === defaultOrder[index]);

  const orders = withoutEmpty(
    (settings[columnOrderKey] ?? {}) as TableColumnSettings,
    tableName,
    isDefault ? emptyColumnOrder : next,
    (order) => (order as ColumnOrderState).length === 0
  ) as NonNullable<TableColumnSettings['columnOrder']>;

  return withoutEmpty(
    settings,
    columnOrderKey,
    orders,
    (stored) => Object.keys(stored).length === 0
  );
};

/**
 * Merges the settings the server still holds under the ones written locally.
 *
 * The write replaces the whole document, and the local copy is resolved against
 * the SWR cache — which is equally empty when the user has saved nothing and
 * when the read failed. A plain top-level spread would be enough for the
 * per-table visibility entries, but `columnOrder` holds every table's order
 * behind one key, so it has to be merged a level deeper or a visibility toggle
 * would carry only the order of the table it touched.
 *
 * Local always wins; a table (or key) only one side knows about is kept as-is,
 * including one written by a newer bundle in another tab.
 */
export const mergeTableColumnSettings = (
  serverSettings: TableColumnSettings | null | undefined,
  localSettings: TableColumnSettings
): TableColumnSettings => {
  const merged: TableColumnSettings = { ...serverSettings, ...localSettings };
  const orders = {
    ...serverSettings?.[columnOrderKey],
    ...localSettings[columnOrderKey],
  };

  return Object.keys(orders).length > 0
    ? { ...merged, [columnOrderKey]: orders }
    : merged;
};
